namespace $ {

	export type $shm_hitalama_map_history_event_packed = {
		v: number
		time: number
		author: string
		peer: string
		targets: readonly string[]
		data: $shm_hitalama_map_history_data
	}

	export class $shm_hitalama_map_history_event extends $giper_baza_entity.with({
		Packed: $giper_baza_atom_dict,
		Parts: $giper_baza_dict_to( $giper_baza_atom_text ),
		Storage: $giper_baza_atom_dict,
		Indexed_targets: $giper_baza_list_str,
		Index_counts: $giper_baza_dict_to( $giper_baza_atom_real ),
		Index_applied: $giper_baza_dict_to( $giper_baza_atom_bool ),
		Time: $giper_baza_atom_real,
		Author: $giper_baza_atom_text,
		Peer: $giper_baza_atom_text,
		Data: $giper_baza_atom_dict,
		Targets: $giper_baza_list_str,
	}) {

		packed_header() { return this.Packed()?.val() }

		@ $mol_mem_key
		packed_parts( text: string ) {
			const parts: { key: string, text: string }[] = []
			for( let from = 0; from < text.length; ) {
				// At most 48 KB UTF-8; never cut a surrogate pair in half.
				let until = Math.min( text.length, from + 16000 )
				if( until < text.length && /[\uD800-\uDBFF]/.test( text[until - 1] ) && /[\uDC00-\uDFFF]/.test( text[until] ) ) --until
				const part = text.slice( from, until )
				parts.push({ key: $giper_baza_link.hash_str( part ).str, text: part })
				from = until
			}
			return parts
		}

		/** Resolve immutable part keys once, preserving the dictionary's first match. */
		@ $mol_action
		packed_part_slots( parts: readonly { key: string, text: string }[] ) {
			const storage = this.Parts( null )!
			// Freeze this command's initial membership across suspension. Re-reading
			// it after each insertion both rescans the growing dictionary and shifts
			// pending post calls when a wire task resumes.
			const units = $mol_wire_sync( storage ).units()
			const land = storage.land()
			const by_key = new Map<string, $giper_baza_unit_sand>()
			for( const unit of units ) {
				const key = land.sand_decode( unit )
				if( typeof key === 'string' && !by_key.has( key ) ) by_key.set( key, unit )
			}
			return parts.map( part=> {
				let unit = by_key.get( part.key )
				if( !unit ) {
					unit = $mol_wire_sync( land ).post( $giper_baza_link.hole, storage.head(), null, part.key, $giper_baza_atom_text.tag )
					by_key.set( part.key, unit )
				}
				return land.Pawn( $giper_baza_atom_text ).Head( unit.self() )
			} )
		}

		/** Pure preparation, retained by the calling command across suspended writes. */
		packed_plan( next: $shm_hitalama_map_history_event_packed ) {
			const text = JSON.stringify( next )
			const bytes = $mol_charset_encode( text ).byteLength
			if( bytes <= 48000 ) return { header: next, parts: null, external: false }
			const parts = this.packed_parts( text )
			const header = { v: 3, time: next.time, author: next.author, peer: next.peer,
				parts: parts.map( part=> part.key ), hash: $giper_baza_link.hash_str( text ).str }
			if( $mol_charset_encode( JSON.stringify( header ) ).byteLength > 48000 ) throw new Error( 'Событие истории слишком велико для индекса частей' )
			return { header, parts, external: bytes > 512 * 1024 }
		}

		@ $mol_action
		packed_external_slots( parts: readonly { key: string, text: string }[] ) {
			const slots: $giper_baza_atom_text[] = []
			const pages: string[] = []
			// Eight parts contain at most 384 KiB UTF-8. A large bulk operation
			// remains one undo event, without making its payload land arbitrarily big.
			for( let from = 0; from < parts.length; from += 8 ) {
				const land = this.land().area_make( $mol_hash_string( `${ this.link().str }:history-content:${ from / 8 }` ) )
				const page = land.Data( $shm_hitalama_map_history_event_parts )
				pages.push( page.link().str )
				const units = $mol_wire_sync( page ).units()
				const by_key = new Map<string, $giper_baza_unit_sand>()
				for( const unit of units ) {
					const key = land.sand_decode( unit )
					if( typeof key === 'string' && !by_key.has( key ) ) by_key.set( key, unit )
				}
				for( const part of parts.slice( from, from + 8 ) ) {
					let unit = by_key.get( part.key )
					if( !unit ) {
						unit = $mol_wire_sync( land ).post( $giper_baza_link.hole, page.head(), null, part.key, $giper_baza_atom_text.tag )
						by_key.set( part.key, unit )
					}
					slots.push( land.Pawn( $giper_baza_atom_text ).Head( unit.self() ) )
				}
			}
			return { slots, pages }
		}

		packed( next?: $shm_hitalama_map_history_event_packed ): $shm_hitalama_map_history_event_packed | undefined {
			let packed = this.Packed()
			if( !packed && next !== undefined ) {
				this.Packed( null )
				packed = this.Packed()
			}
			if( next !== undefined ) {
				const plan = $mol_wire_sync( this ).packed_plan( next )
				if( !plan.parts ) packed!.val( plan.header )
				else {
					const parts = plan.parts
					const external = plan.external ? $mol_wire_sync( this ).packed_external_slots( parts ) : null
					const header = external ? { ...plan.header, v: 4, pages: external.pages } : plan.header
					if( $mol_charset_encode( JSON.stringify( header ) ).byteLength > 48000 ) throw new Error( 'Событие истории слишком велико для индекса частей' )
					const slots = external?.slots ?? $mol_wire_sync( this ).packed_part_slots( parts )
					for( let index = 0; index < parts.length; ++index ) slots[index].val( parts[index].text )
					// Publish only after all content-addressed parts are present.
					packed!.val( header )
				}
				return next
			}
			return this.packed_read()
		}

		@ $mol_mem
		packed_read(): $shm_hitalama_map_history_event_packed | undefined {
			const header = this.packed_header()
			if( header?.v !== 3 && header?.v !== 4 ) return header as $shm_hitalama_map_history_event_packed | undefined
			if( !Array.isArray( header.parts ) || header.parts.some( key=> typeof key !== 'string' ) ) return undefined
			if( header.v === 4 && ( !Array.isArray( header.pages ) || header.pages.length !== Math.ceil( header.parts.length / 8 ) || header.pages.some( link=> typeof link !== 'string' ) ) ) return undefined
			const pages = header.v === 4 ? header.pages as string[] : null
			const parts: string[] = []
			for( let index = 0; index < header.parts.length; ++index ) {
				const key = header.parts[ index ]
				const page = header.v === 4
					? this.$.$giper_baza_glob.Pawn( new $giper_baza_link( pages![ Math.floor( index / 8 ) ] ), $shm_hitalama_map_history_event_parts )
					: this.Parts()
				const part = page?.key( key )?.val()
				if( part === undefined || part === null || $giper_baza_link.hash_str( part ).str !== key ) return undefined
				parts.push( part )
			}
			const text = parts.join( '' )
			if( $giper_baza_link.hash_str( text ).str !== header.hash ) return undefined
			return JSON.parse( text ) as $shm_hitalama_map_history_event_packed
		}

		storage( next?: { writer: string, page: number } ) {
			let storage = this.Storage()
			if( !storage && next !== undefined ) {
				this.Storage( null )
				storage = this.Storage()
			}
			return storage?.val( next ) as typeof next
		}

		indexed_targets() {
			return this.Indexed_targets()?.items() ?? []
		}

		/** Durable intent: retries of one command reuse its planned counter. */
		@ $mol_action
		index_count_plan( target: string, initial: number ) {
			const saved = this.Index_counts()?.key( target )?.val()
			if( saved !== null && saved !== undefined ) {
				if( !Number.isSafeInteger( saved ) || saved < 1 ) throw new Error( 'Повреждён план счётчика истории' )
				return saved
			}
			if( !Number.isSafeInteger( initial ) || initial < 1 ) throw new Error( 'Некорректный счётчик истории' )
			this.Index_counts( null )!.key( target, 'auto' )!.val( initial )
			return initial
		}

		indexed_target_self( target: string, head: string ) {
			return $giper_baza_link.hash_str( `${head}:history-indexed:${target}` ).peer()
		}

		index_count_applied( target: string, next?: boolean ) {
			if( next !== undefined ) return this.Index_applied( null )!.key( target, 'auto' )!.val( next )!
			return this.Index_applied()?.key( target )?.val() ?? false
		}

		/** Caller has checked membership; checkpoint order has no meaning. */
		@ $mol_action
		indexed_target_mark( target: string ) {
			let list = this.Indexed_targets()
			if( !list ) list = this.Indexed_targets( null )
			if( !list ) throw new Error( 'Не удалось открыть список индексов события' )
			// Like list.add(), without its redundant membership scan or splice's
			// reconciliation of every previously recorded checkpoint.
			// Two deliveries can pass the caller's membership check concurrently.
			// Stable Unit identity makes both write one checkpoint without a scan.
			const self = $mol_wire_sync( this ).indexed_target_self( target, list.head().str )
			const land = list.land()
			const existing = land.sand_get( list.head(), land.auth().pass().lord(), self )
			if( existing && land.sand_decode( existing ) === target ) return existing
			return $mol_wire_sync( land ).post( $giper_baza_link.hole, list.head(), self, target )
		}

		time( next?: number ) {
			if( next === undefined ) return Number( this.packed_header()?.time ?? this.Time()?.val() ?? 0 )
			return this.Time( next )?.val( next ) ?? 0
		}

		author( next?: string ) {
			if( next === undefined ) return String( this.packed_header()?.author ?? this.Author()?.val() ?? '' )
			return this.Author( next )?.val( next ) ?? ''
		}

		peer( next?: string ) {
			if( next === undefined ) return String( this.packed_header()?.peer ?? this.Peer()?.val() ?? '' )
			return this.Peer( next )?.val( next ) ?? ''
		}

		data( next?: $shm_hitalama_map_history_data ) {
			if( next === undefined ) {
				const packed = this.packed()
				const version = this.packed_header()?.v
				if( version === 3 || version === 4 ) return packed?.data
				return packed?.data ?? this.Data()?.val() as $shm_hitalama_map_history_data | undefined
			}
			return this.Data( next )?.val( next ) as $shm_hitalama_map_history_data | undefined
		}

		@ $mol_mem
		private loading() {
			return { promise: new $mol_promise_blocker<void>() }
		}

		/** Explicit operations require the payload, not the already arrived summary. */
		@ $mol_mem
		data_ready() {
			this.keys()
			const data = this.data()
			if( !data ) $mol_fail_hidden( this.loading().promise )
			return data!
		}

		targets( next?: readonly string[] ) {
			if( next === undefined ) {
				const packed = this.packed()
				const version = this.packed_header()?.v
				if( version === 3 || version === 4 ) return packed?.targets ?? []
				return packed?.targets ?? this.Targets()?.items() as readonly string[] ?? []
			}
			return this.Targets( next as string[] | undefined )?.items( next as string[] | undefined ) as readonly string[] ?? []
		}

	}

}
